import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { format } from 'date-fns';

/**
 * AuditLogPanel — dedicated "View Audit Log" section for assignment detail pages.
 *
 * Props:
 *   assignmentId   — the assignment to load history for
 *   fetchHistory   — async fn(id) → ApiResponse<AuditLogResponse[]>
 *   showRawPerformer — if true, shows email/username; if false, shows role label (default false)
 *   queryKey       — react-query cache key prefix (default "audit-log")
 */
export default function AuditLogPanel({
  assignmentId,
  fetchHistory,
  showRawPerformer = false,
  queryKey = 'audit-log',
}) {
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useQuery(
    [queryKey, assignmentId],
    () => fetchHistory(assignmentId),
    { enabled: open && !!assignmentId && !!fetchHistory, refetchOnWindowFocus: false }
  );

  const entries = data?.data || [];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      {/* ── Header row ────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <h3 className="text-sm font-semibold text-gray-800">Audit Log</h3>
          <span className="text-xs text-gray-400 font-normal">
            — full record of every action on this assignment
          </span>
        </div>
        <button
          onClick={() => setOpen(v => !v)}
          className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 px-2.5 py-1 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-3.5 w-3.5">
            <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" />
          </svg>
          {open ? 'Hide Audit Log' : 'View Audit Log'}
        </button>
      </div>

      {/* ── Log entries ───────────────────────────────────────────────── */}
      {open && (
        <div className="mt-4 border-t border-gray-100 pt-4">
          {isLoading ? (
            <p className="text-xs text-gray-400 text-center py-4">Loading audit log…</p>
          ) : entries.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">No activity recorded yet.</p>
          ) : (
            <div className="space-y-3">
              {[...entries].reverse().map((entry) => (
                <AuditEntry
                  key={entry.id}
                  entry={entry}
                  showRawPerformer={showRawPerformer}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Single audit entry ────────────────────────────────────────────────────────

const BOM_EDIT_ACTIONS = new Set([
  'BOM_UPDATED_BY_ADMIN',
  'BOM_UPDATED_BY_CLIENT',
]);

const BOM_DELETE_ACTIONS = new Set([
  'BOM_COMPONENT_DELETED_BY_ADMIN',
  'BOM_COMPONENT_DELETED_BY_CLIENT',
  'BOM_COMPONENT_DELETED_BY_VENDOR',
]);

function AuditEntry({ entry, showRawPerformer }) {
  const isBomEdit   = BOM_EDIT_ACTIONS.has(entry.action);
  const isBomDelete = BOM_DELETE_ACTIONS.has(entry.action);
  const [expandDiff, setExpandDiff] = useState(false);

  // Try to parse JSON diff for both edit and delete events
  let diffRows = null;
  if ((isBomEdit || isBomDelete) && entry.details) {
    try {
      const parsed = JSON.parse(entry.details);
      if (Array.isArray(parsed)) diffRows = parsed;
    } catch (_) {
      // fall through — render as plain text
    }
  }

  const { color, dot, label } = resolveStyle(entry.action);
  const role      = roleLabel(entry.action, entry.performedBy);
  const email     = entry.performedBy || null;
  const timestamp = entry.performedAt
    ? format(new Date(entry.performedAt), 'dd MMM yyyy, HH:mm')
    : '—';

  // For delete events: extract component name from first row's field prefix (e.g. "Seat Motor · Part Name")
  const deletedComponentName = isBomDelete && diffRows?.length
    ? (diffRows[0].field || '').split(' · ')[0] || 'BOM Component'
    : null;

  return (
    <div className="flex gap-3 items-start">
      {/* Dot */}
      <div className={`mt-1.5 h-2.5 w-2.5 rounded-full flex-shrink-0 ${dot}`} />

      <div className="flex-1 min-w-0">
        {/* Action line */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-xs font-semibold ${color}`}>{label}</span>
          <span className="text-xs text-gray-400">
            by{' '}
            <span className="text-gray-700 font-medium">{role}</span>
            {email && (
              <span className="text-gray-400 font-normal"> · {email}</span>
            )}
          </span>
          <span className="text-xs text-gray-400 flex items-center gap-1">
            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {timestamp}
          </span>
        </div>

        {/* BOM delete — collapsible field snapshot */}
        {isBomDelete && diffRows && (
          <div className="mt-1.5">
            <button
              onClick={() => setExpandDiff(v => !v)}
              className="text-xs text-red-600 hover:text-red-800 underline underline-offset-2 font-medium">
              {expandDiff
                ? 'Hide deleted fields'
                : `"${deletedComponentName}" — show deleted fields`}
            </button>
            {expandDiff && (() => {
              const visibleRows = diffRows.filter(row => row.oldValue && row.oldValue !== '—');
              return (
                <div className="mt-2 rounded-lg border border-red-100 overflow-x-auto">
                  <table className="text-xs whitespace-nowrap">
                    <thead>
                      <tr className="bg-red-50 border-b border-red-100">
                        {visibleRows.map((row, i) => {
                          const fieldLabel = (row.field || '').includes(' · ')
                            ? row.field.split(' · ').slice(1).join(' · ')
                            : row.field;
                          return (
                            <th key={i} className="px-3 py-1.5 text-left font-semibold text-gray-500 uppercase tracking-wide border-r border-red-100 last:border-r-0">
                              {fieldLabel}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {visibleRows.map((row, i) => (
                          <td key={i} className="px-3 py-2 border-r border-red-50 last:border-r-0">
                            <span className="inline-block px-1.5 py-0.5 bg-red-50 border border-red-100 rounded text-red-700 font-mono">
                              {row.oldValue}
                            </span>
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        )}

        {/* BOM delete with no parseable diff (fallback) */}
        {isBomDelete && !diffRows && entry.details && (
          <p className="text-xs text-gray-500 mt-0.5">{entry.details}</p>
        )}

        {/* BOM edit diff — collapsible table */}
        {isBomEdit && diffRows && (
          <div className="mt-1.5">
            <button
              onClick={() => setExpandDiff(v => !v)}
              className="text-xs text-blue-600 hover:text-blue-800 underline underline-offset-2 font-medium">
              {expandDiff
                ? 'Hide changes'
                : `${diffRows.length} field${diffRows.length !== 1 ? 's' : ''} changed — show`}
            </button>
            {expandDiff && (
              <div className="mt-2 rounded-lg border border-gray-100 overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="px-3 py-1.5 text-left font-semibold text-gray-500 uppercase tracking-wide">Field</th>
                      <th className="px-3 py-1.5 text-left font-semibold text-gray-500 uppercase tracking-wide">Before</th>
                      <th className="px-3 py-1.5 text-left font-semibold text-gray-500 uppercase tracking-wide">After</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {diffRows.map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50/60">
                        <td className="px-3 py-1.5 text-gray-600 font-medium whitespace-nowrap">{row.field}</td>
                        <td className="px-3 py-1.5 max-w-[180px]">
                          <span className="inline-block px-1.5 py-0.5 bg-red-50 border border-red-100 rounded text-red-700 font-mono break-words">
                            {row.oldValue}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 max-w-[180px]">
                          <span className="inline-block px-1.5 py-0.5 bg-green-50 border border-green-100 rounded text-green-700 font-mono break-words">
                            {row.newValue}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Non-BOM details (plain text) */}
        {!isBomEdit && !isBomDelete && entry.details && entry.details !== 'No fields changed' && (
          <p className="text-xs text-gray-500 mt-0.5 break-words">{entry.details}</p>
        )}

        {/* BOM edit with no parseable diff (fallback) */}
        {isBomEdit && !diffRows && entry.details && (
          <p className="text-xs text-gray-500 mt-0.5">{entry.details}</p>
        )}
      </div>
    </div>
  );
}

// ── Style helpers ─────────────────────────────────────────────────────────────

function resolveStyle(action) {
  switch (action) {
    case 'COMPANY_APPROVED':
    case 'CLIENT_APPROVED':
      return { color: 'text-green-600',  dot: 'bg-green-500',  label: formatAction(action) };
    case 'COMPANY_REJECTED':
    case 'CLIENT_REJECTED':
    case 'CVA_REJECTED':
    case 'CANCELLED':
      return { color: 'text-red-600',    dot: 'bg-red-500',    label: formatAction(action) };
    case 'VENDOR_SUBMITTED':
      return { color: 'text-blue-600',   dot: 'bg-blue-500',   label: formatAction(action) };
    case 'BOM_UPDATED_BY_ADMIN':
    case 'BOM_UPDATED_BY_CLIENT':
      return { color: 'text-amber-600',  dot: 'bg-amber-400',  label: formatAction(action) };
    case 'BOM_COMPONENT_DELETED_BY_ADMIN':
    case 'BOM_COMPONENT_DELETED_BY_CLIENT':
    case 'BOM_COMPONENT_DELETED_BY_VENDOR':
      return { color: 'text-red-600',    dot: 'bg-red-400',    label: formatAction(action) };
    case 'NOTE_ADDED':
      return { color: 'text-gray-500',   dot: 'bg-gray-300',   label: formatAction(action) };
    default:
      return { color: 'text-gray-700',   dot: 'bg-gray-400',   label: formatAction(action) };
  }
}

function formatAction(action) {
  if (!action) return '—';
  const MAP = {
    COMPANY_APPROVED:       'OmniDrive Approved',
    COMPANY_REJECTED:       'OmniDrive Rejected',
    CLIENT_APPROVED:        'Fully Approved (CVA)',
    CLIENT_REJECTED:        'Account Rejected',
    CVA_REJECTED:           'CVA Rejected',
    VENDOR_SUBMITTED:       'Vendor Submitted BOM',
    ASSIGNED:               'Assigned to Vendor',
    WITHDRAWN:              'Assignment Withdrawn',
    CANCELLED:              'Process Cancelled',
    DRAFT_SAVED:            'Draft Saved by Vendor',
    NOTE_ADDED:             'Note Added',
    BOM_UPDATED_BY_ADMIN:              'BOM Edited by OmniDrive',
    BOM_UPDATED_BY_CLIENT:             'BOM Edited by Account',
    BOM_COMPONENT_DELETED_BY_ADMIN:    'BOM Component Deleted by OmniDrive',
    BOM_COMPONENT_DELETED_BY_CLIENT:   'BOM Component Deleted by Account',
    BOM_COMPONENT_DELETED_BY_VENDOR:   'BOM Component Deleted by Vendor',
  };
  return MAP[action] || action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function roleLabel(action, performedBy) {
  if (!performedBy) return '—';
  if (['COMPANY_APPROVED', 'COMPANY_REJECTED', 'ASSIGNED',
       'BOM_UPDATED_BY_ADMIN', 'BOM_COMPONENT_DELETED_BY_ADMIN'].includes(action)) return 'OmniDrive';
  if (action === 'CLIENT_APPROVED' || action === 'CVA_REJECTED')      return 'CVA';
  if (['CLIENT_REJECTED', 'BOM_UPDATED_BY_CLIENT',
       'BOM_COMPONENT_DELETED_BY_CLIENT'].includes(action))           return 'Account';
  if (['VENDOR_SUBMITTED', 'DRAFT_SAVED',
       'BOM_COMPONENT_DELETED_BY_VENDOR'].includes(action))           return 'Vendor';
  const lower = performedBy.toLowerCase();
  if (lower.includes('omnidrive') || lower.includes('admin@')) return 'OmniDrive';
  return 'Vendor';
}
